import { useEffect, useRef } from 'react';
import { ensureSecondaryAuthSynced, getDbSupabase } from '../lib/supabase';

const DEBOUNCE_MS = 400;

type UsePoOrdersRealtimeOptions = {
  enabled?: boolean;
  onChange?: () => void;
};

/**
 * Live-refresh DEM Orders when orders / lines / batches change.
 * Requires migration 247 (tables on supabase_realtime publication).
 */
export function usePoOrdersRealtime(options: UsePoOrdersRealtimeOptions = {}) {
  const { enabled = true, onChange } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let channel: ReturnType<ReturnType<typeof getDbSupabase>['channel']> | null = null;

    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        onChangeRef.current?.();
      }, DEBOUNCE_MS);
    };

    void (async () => {
      // Secondary DEM client needs JWT before Realtime RLS filters apply.
      await ensureSecondaryAuthSynced(4_000);
      if (cancelled) return;

      const db = getDbSupabase('dem');
      channel = db
        .channel(`dem-orders-live:${Date.now().toString(36)}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'data_entry_management', table: 'orders' },
          schedule,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'data_entry_management', table: 'order_items' },
          schedule,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'data_entry_management', table: 'order_batches' },
          schedule,
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      if (channel) {
        void getDbSupabase('dem').removeChannel(channel);
      }
    };
  }, [enabled]);
}
